import React, { useEffect, useState, useCallback } from "react";
import config from "appConfig";
import styles from "../styles/CreditPackages.scss";
import BuyButton from "./BuyButton";
import history from "../../../history";
import { checkEmptyArray, checkEmptyObject, localStorage, notification } from "../../../services";
import { HashTag, LocalStorageKey, PageRoute, PaymentMethod, PaymentMethodText, PaymentMethodTextInterface, ShoppingCart, ShoppingCartItemType, ShoppingCartResponse, ShoppingCartStatus } from "../../../models";
import { Form, Radio } from "antd";
import { requiredField } from "../../../models/formValidator";
import { useDispatch, useSelector } from "react-redux";
import { getCredits, selectCredits } from "../../../routes/Credit/credit.ducks";
import { CreditPackageInterface, UserI } from "../../../typings/interfaces";
import { createCart } from "../../../routes/Cart/cart.ducks";
import confirmModal from "../../../services/confirmModal";
import BankTransfer from "../../../components/BankTransfer";
import { redirectByPost } from "../../../utils/RedirectByPost";
import { getImageUrl } from "../../../services/asset";
import { disableScrollRestoration, restoreScrollRestoration } from "../../../utils/DisableScrollRestoration";
import { CreditsInfoModal } from "../CreditsInfoModal/CreditsInfoModal";
import { InfoCircleOutlined } from "@ant-design/icons";
import Skeleton from "../../../components/Skeleton";
import { CreditPackagesLoading } from "./CreditPackagesLoading";

export interface ICreditPackagesProps {
  user: UserI;
  adData: any;
  settings: any;
  form: any;
  creditPackages: CreditPackageInterface[];
  paymentMethods: PaymentMethodTextInterface[];
}

const defaultSettings = {
  formValidator: {
    creditPackage: [requiredField(null, "Molimo odaberite jednu od opcija kako biste nastavili dalje.")],
    paymentMethod: [requiredField(null, "Molimo odaberite jednu od opcija kako biste nastavili dalje.")],
  }
};

const CreditPackages = (props: ICreditPackagesProps) => {
  const {
    user,
    adData,
    settings: { formValidator },
    form,
    paymentMethods,
  } = props;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [isCreditsInfoModalOpen, setIsCreditsInfoModalOpen] = useState(false)
  const dispatch = useDispatch();
  const creditPackages = useSelector(selectCredits);

  useEffect(() => {
    const creditPurchased = history.location.hash === HashTag.creditPurchased;
    const redirect = localStorage.getItem(LocalStorageKey.redirectAfterCreditPurchase);

    if (creditPurchased) {
      notification.show("Kupovina kredita uspješna");

      if (redirect) {
        localStorage.removeItem(LocalStorageKey.redirectAfterCreditPurchase);
        history.push(redirect);
      } else {
        history.push(PageRoute.credits);
      }
    }

    // Disable scroll restoration on Chrome and other modern browsers
    disableScrollRestoration();

    //retun scroll restoration when component unmounts
    return () => {
      restoreScrollRestoration();
    }

  }, []);

  useEffect(() => {
    setIsLoading(true)
    dispatch(getCredits(() => {
      setIsLoading(false)
    }));

  }, []);

  // Handle submit
  const handleSubmit = useCallback(e => {
    e.preventDefault();

    form.validateFields((error, values) => {

      if (!error) {
        const data: ShoppingCart = {
          item: {
            itemId: values.creditPackage,
            adId: null,
            type: ShoppingCartItemType.StoreCredit,
          },
          paymentMethod: values.paymentMethod
        };

        setIsSubmitting(true);

        createCart(data, (cart: ShoppingCartResponse) => {
          setIsSubmitting(false);

          // If cart is not created
          if (!cart) {
            return;
          }

          // @ts-ignore
          const redirect = history.location.state && history.location.state.redirectAfterCreditPurchase;

          if (!redirect) {
            localStorage.removeItem(LocalStorageKey.redirectAfterCreditPurchase);
          }

          // If cart status is failed
          if (cart.status == ShoppingCartStatus.failed) {
            notification.show("Kupovina kredita neuspješna");
          } else {
            // If payment method is bank transfer
            if (
              cart.paymentMethod == PaymentMethod.bankTransfer &&
              Object.values(ShoppingCartItemType).includes(cart.type) &&
              cart.bankTransferInfo &&
              checkEmptyObject(cart.bankTransferInfo)
            ) {
              notification.show("Kupovina kredita čeka plaćanje");

              // open modal with bank transfer instructions , than on OK button click redirect to the details page
              confirmModal.show({
                title: "Podaci za uplatu",
                iconType: "NaN",
                cancelButtonProps: { style: { display: 'none' } }, // hide cancel button
                wrapClassName: "bankTransferModal",
                width: 610,
                onOk() {
                  history.replace(redirect ? redirect : PageRoute.credits);
                },
                onCancel() {
                  history.replace(redirect ? redirect : PageRoute.credits);
                },
                children: <BankTransfer cart={cart} />
              });
            }

            // If payment method is credit card 
            if (
              cart.paymentMethod == PaymentMethod.creditCard &&
              cart.creditCardInfo &&
              cart.creditCardInfo.merchantUrl &&
              cart.creditCardInfo.redirectParameters &&
              checkEmptyObject(cart.creditCardInfo)
            ) {

              if (redirect) {
                localStorage.setItem(LocalStorageKey.redirectAfterCreditPurchase, redirect);
              }

              // create post request to the merchant url
              redirectByPost(cart.creditCardInfo.merchantUrl, cart.creditCardInfo.redirectParameters, false);
            }
          }
        });
      }
    });
  }, [form, adData]);

  if (isLoading) {
    return (
      <div className={styles.content}>
        <div className={styles.actions}>
          <CreditPackagesLoading />
        </div>
      </div>

    )
  }


  return (
    <div className={styles.content}>
      <div className={styles.title}>{"Kupnja kredita"}</div>
      <div className={styles.actions}>
        <Form layout="vertical">
          <div className={styles.section}>
            <p className={styles.subtitle}>{"Na stanju: {0} kredita".replace("{0}", String(user.storeCreditBalance))}</p>
            <p className={styles.description}>
              {"Najjeftiniji i najbrži način isticanja oglasa. Kreditom možeš plaćati istaknute pozicije oglasa, a što veći paket kupiš, to veći popust dobiješ."}
            </p>
            <p>
              <span className={styles.moreInfoLink} onClick={() => setIsCreditsInfoModalOpen(true)}>
                <InfoCircleOutlined />&nbsp;
                <span>Vidi više informacija</span>
              </span>
            </p>
          </div>

          {checkEmptyArray(creditPackages) &&
            <div className={`${styles.section} ${styles.sectionCards}`}>
              <Form.Item>
                {form.getFieldDecorator("creditPackage", {
                  rules: formValidator.creditPackage
                })(<Radio.Group className={styles.creditPackages}>
                  <div className={styles.tiles}>
                    {creditPackages.map(option => (
                      <div
                        className={styles.tileContainer}
                        key={option.id}
                      >
                        <Radio
                          value={option.id}
                          className={`${styles.tile} ${form.getFieldValue("creditPackage") === option.id ? styles.selected : ""}`}
                        >
                          <div className={styles.title}>{option.name}</div>
                          <p>{option.description}</p>
                          <div className={styles.price}>{`${option.price} ${config.currencySymbol}`}</div>
                        </Radio>
                      </div>
                    ))}
                  </div>
                </Radio.Group>)}
              </Form.Item>
            </div>
          }

          {form.getFieldValue("creditPackage") && <React.Fragment>
            {checkEmptyArray(paymentMethods) &&
              <div className={`${styles.section} ${styles.sectionPaymentOptions}`}>
                <p className={styles.subtitle}>{"Opcije plaćanja:"}</p>
                <Form.Item className={styles.formOptions}>
                  {form.getFieldDecorator("paymentMethod", {
                    rules: formValidator.paymentMethod,
                  })(<Radio.Group className={styles.paymentMethods}>
                    {paymentMethods.map((option, index) => (
                      <React.Fragment key={index}>
                        <Radio value={option.value}
                          className={`${styles.item} ${(form.getFieldValue("paymentMethod") == PaymentMethod.creditCard && option.value == PaymentMethod.creditCard) && styles.creditCardFocued}`}
                          key={option.value}>
                          <div className={styles.tile}>
                            <span className={styles.title}>{option.name}</span>
                            <span className={styles.desc}>
                              <span>({option.description})</span>
                            </span>
                          </div>
                        </Radio>

                        {(form.getFieldValue("paymentMethod") == PaymentMethod.creditCard && option.value == PaymentMethod.creditCard) &&
                          <div className={styles.creditCardOption}>
                            <img className={styles.creditCardImg} src={getImageUrl("corvus-logo.png")} alt="CorvusPay" />
                            <p className={styles.creditCardTxt}>{"Pri plaćanju na našoj web trgovini koristite CorvusPay – napredni sustav za siguran prihvat platnih kartica putem interneta. CorvusPay sustav osigurava potpunu tajnost Vaših kartičnih i osobnih podataka već od trenutka kada ih upišete u CorvusPay platni formular. Platni podaci prosljeđuju se šifrirano od Vašeg web preglednika do banke koja je izdala Vašu karticu. Naša trgovina nikada ne dolazi u kontakt s cjelovitim podacima o Vašoj platnoj kartici. Također, podaci su nedostupni čak i djelatnicima CorvusPay sustava. Izolirana jezgra samostalno prenosi i upravlja osjetljivim podacima, čuvajući ih pri tom potpuno sigurnima."}</p>
                          </div>
                        }
                      </React.Fragment>
                    ))}
                  </Radio.Group>)}
                </Form.Item>
              </div>
            }
          </React.Fragment>}
          <div className={`${styles.section} ${styles.sectionActions}`}>
            <BuyButton
              onClick={handleSubmit}
              isEnable={true}
              isLoading={isSubmitting}
            />
          </div>
        </Form>
      </div>
      <CreditsInfoModal visible={isCreditsInfoModalOpen} onCancel={() => setIsCreditsInfoModalOpen(false)} />
    </div>
  );
};

CreditPackages.defaultProps = {
  settings: defaultSettings,
  creditPackages: [],
  paymentMethods: PaymentMethodText.filter(item => item.value !== PaymentMethod.storeCredit),
};

export default Form.create()(CreditPackages);



// WEBPACK FOOTER //
// ./src/containers/CreditsPage/CreditPackages/index.tsx